import React, { useCallback, useMemo } from 'react'
import { Checkbox, Popconfirm } from 'antd'
import { includes } from 'lodash'

import { StyledButtonWithMargin } from '../../../common/components/styled'
import { PERMISSIONS } from '../../../common/constants'

import { useUserHasPermissions } from '../../../account/hooks'
import { BRAND_CURATION_TASK_STATUS } from '../../constants'
import {
  useReopenBrandCurationTaskMutation,
  useUpdateBrandCurationTaskStatusFlagMutation,
  useUserHasBrandCurationTaskChangePermission,
} from '../../hooks'
import { brandCurationTaskPropType } from '../../prop-types'

const BrandCurationTaskMenuButtons = ({ brandCurationTask }) => {
  const { userHasBrandChangePermission } =
    useUserHasBrandCurationTaskChangePermission(brandCurationTask)

  const [reopen, { isLoading }] = useReopenBrandCurationTaskMutation()

  const [updateBrandCurationTaskStatusFlag] =
    useUpdateBrandCurationTaskStatusFlagMutation()

  const hasPermissionToChangeStatusFlags = useUserHasPermissions(
    PERMISSIONS.BRAND_CURATION_TASK.UPDATE_STATUS_FLAGS,
  )

  const hasPermissionToReopen = useUserHasPermissions(
    PERMISSIONS.BRAND_CURATION_TASK.REOPEN,
  )

  const handleReopen = useCallback(
    () => reopen(brandCurationTask.id),
    [brandCurationTask.id, reopen],
  )
  const handleCurationDone = useCallback(() => {
    updateBrandCurationTaskStatusFlag(brandCurationTask.id, {
      isCurationDone: !brandCurationTask.isCurationDone,
    })
  }, [
    brandCurationTask.id,
    brandCurationTask.isCurationDone,
    updateBrandCurationTaskStatusFlag,
  ])

  const handleQaDone = useCallback(() => {
    updateBrandCurationTaskStatusFlag(brandCurationTask.id, {
      isQaDone: !brandCurationTask.isQaDone,
    })
  }, [
    brandCurationTask.id,
    brandCurationTask.isQaDone,
    updateBrandCurationTaskStatusFlag,
  ])

  const handleQcDone = useCallback(() => {
    updateBrandCurationTaskStatusFlag(brandCurationTask.id, {
      isQcDone: !brandCurationTask.isQcDone,
    })
  }, [
    brandCurationTask.id,
    brandCurationTask.isQcDone,
    updateBrandCurationTaskStatusFlag,
  ])

  const disabledByStatus = useMemo(
    () =>
      includes(
        [
          BRAND_CURATION_TASK_STATUS.RESOLVED.value,
          BRAND_CURATION_TASK_STATUS.AUTOMATICALLY_RESOLVED.value,
        ],
        brandCurationTask?.taskStatus,
      ),
    [brandCurationTask?.taskStatus],
  )

  const areStatusFlagCheckboxesDisabled = useMemo(
    () => !hasPermissionToChangeStatusFlags || !userHasBrandChangePermission,
    [hasPermissionToChangeStatusFlags, userHasBrandChangePermission],
  )

  return (
    <>
      {includes(
        [
          BRAND_CURATION_TASK_STATUS.RESOLVED.value,
          BRAND_CURATION_TASK_STATUS.AUTOMATICALLY_RESOLVED.value,
        ],
        brandCurationTask?.taskStatus,
      ) && (
        <Popconfirm
          title="This will Reopen the Task. Are you sure?"
          onConfirm={handleReopen}
          disabled={!hasPermissionToReopen}
        >
          <StyledButtonWithMargin
            loading={isLoading}
            disabled={!hasPermissionToReopen}
          >
            Reopen
          </StyledButtonWithMargin>
        </Popconfirm>
      )}
      <Popconfirm
        title="This will confirm Curation Done. Are you sure?"
        onConfirm={handleCurationDone}
        disabled={
          disabledByStatus ||
          areStatusFlagCheckboxesDisabled ||
          brandCurationTask.isCurationDone
        }
      >
        <Checkbox
          disabled={
            disabledByStatus ||
            areStatusFlagCheckboxesDisabled ||
            brandCurationTask.isCurationDone
          }
          checked={brandCurationTask.isCurationDone}
        >
          Curation Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleQaDone}
        disabled={
          disabledByStatus ||
          areStatusFlagCheckboxesDisabled ||
          !brandCurationTask.isCurationDone
        }
        title="This will confirm QA Done. Are you sure?"
      >
        <Checkbox
          disabled={
            disabledByStatus ||
            areStatusFlagCheckboxesDisabled ||
            !brandCurationTask.isCurationDone ||
            brandCurationTask.isQaDone
          }
          checked={brandCurationTask.isQaDone}
        >
          Qa Done
        </Checkbox>
      </Popconfirm>
      <Popconfirm
        onConfirm={handleQcDone}
        disabled={
          areStatusFlagCheckboxesDisabled ||
          !brandCurationTask.isQaDone ||
          brandCurationTask.isQcDone
        }
        title="This will confirm QC Done. Are you sure?"
      >
        <Checkbox
          disabled={
            areStatusFlagCheckboxesDisabled ||
            !brandCurationTask.isQaDone ||
            brandCurationTask.isQcDone
          }
          checked={brandCurationTask.isQcDone}
        >
          Qc Done
        </Checkbox>
      </Popconfirm>
    </>
  )
}

BrandCurationTaskMenuButtons.propTypes = {
  brandCurationTask: brandCurationTaskPropType.isRequired,
}

export default BrandCurationTaskMenuButtons
